import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { ArrowLeft, X } from 'lucide-react'

import { cn } from '@/common/utils/utils'
import IntercomVisibilityController from './IntercomVisibilityController'
import { Button } from './button'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay
        ref={ref}
        className={cn(
            'fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            className,
        )}
        {...props}
    />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
    onClose?: () => void
    onBack?: () => void
    open?: boolean
}
const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(
    ({ className, children, onClose, onBack, ...props }, ref) => {
        // Extract DialogFooter from children for mobile rendering
        const childrenArray = React.Children.toArray(children)
        const footerChild = childrenArray.find(
            (child) =>
                React.isValidElement(child) &&
                (child.type === DialogFooter || (child.type && (child.type as any).displayName === 'DialogFooter')),
        )
        const contentChildren = childrenArray.filter(
            (child) =>
                !(
                    React.isValidElement(child) &&
                    (child.type === DialogFooter || (child.type && (child.type as any).displayName === 'DialogFooter'))
                ),
        )

        return (
            <DialogPortal>
                <IntercomVisibilityController open={!!props.open} />
                <DialogOverlay />
                <DialogPrimitive.Content
                    ref={ref}
                    className={cn(
                        // Mobile: full-screen flex layout (bounded height).
                        'fixed inset-0 sm:inset-auto z-50 flex flex-col bg-background rounded-none p-0 overflow-hidden',
                        // Desktop: positioned and transformed to center
                        'sm:left-[50%] sm:top-[50%] sm:z-50 sm:w-full sm:min-w-lg sm:translate-x-[-50%] sm:translate-y-[-50%] sm:border sm:bg-background sm:shadow-lg sm:duration-200',
                        // Desktop: use a 3-row grid so header/content/footer are laid out correctly
                        // Constrain the dialog height so tall dialogs scroll internally without forcing a fixed height
                        'sm:grid sm:grid-rows-[auto_1fr_auto] sm:max-h-[85vh] sm:overflow-hidden',
                        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                        'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
                        'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
                        'sm:rounded-lg sm:rounded-tl-[80px] sm:rounded-br-[80px] pb-2 sm:pb-10 ',
                        className,
                    )}
                    {...props}
                >
                    <div className="absolute top-0 w-full self-end sm:pb-0 flex flex-row items-center justify-between p-8 sm:static">
                        {onBack && (
                            <Button
                                variant={'tertiary'}
                                onClick={onBack}
                                className="sm:hidden absolute left-4 top-4 w-8 h-8 p-0 rounded-full flex items-center justify-center"
                            >
                                <ArrowLeft className="h-4 w-4" />
                            </Button>
                        )}
                        {onClose && (
                            <DialogPrimitive.Close
                                className="absolute right-4 top-4 w-8 h-8 p-0 rounded-full flex items-center justify-center opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground bg-primary-dust sm:static sm:ml-auto"
                                onClick={onClose}
                            >
                                <X className="h-4 w-4" />
                                <span className="sr-only">Close</span>
                            </DialogPrimitive.Close>
                        )}
                    </div>
                    {/* Content area, with padding for mobile to avoid overlap with footer */}

                    {footerChild ? (
                        <div className={'flex-1 min-h-0 overflow-y-auto sm:h-full sm:px-10 px-6 pb-[120px]'}>
                            {contentChildren}
                        </div>
                    ) : (
                        contentChildren
                    )}
                    {/* Footer fixed to bottom on mobile, grid row on desktop */}
                    {footerChild && (
                        // Mobile: keep footer fixed to bottom (absolute). Desktop (sm+): make footer part of the grid (static)
                        <div
                            className={cn(
                                'w-full absolute bottom-0 left-0 right-0 bg-background z-[60] p-4',
                                'sm:static sm:bg-transparent sm:p-0 sm:z-auto shadow-[0px_0px_16px_0px_rgba(0,0,0,0.16)] sm:shadow-none',
                            )}
                        >
                            {footerChild}
                        </div>
                    )}
                </DialogPrimitive.Content>
            </DialogPortal>
        )
    },
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div className={cn('self-start flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...props} />
)

DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Title
        ref={ref}
        className={cn('text-lg font-semibold leading-none tracking-tight', className)}
        {...props}
    />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
    Dialog,
    DialogPortal,
    DialogOverlay,
    DialogClose,
    DialogTrigger,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
}
